
import { Directive, ElementRef, HostListener, Inject, Input, OnDestroy, Renderer2, DOCUMENT } from '@angular/core';

@Directive({
    selector: '[appCopyToClipboard]',
    standalone: false
})
export class CopyToClipboardDirective implements OnDestroy {
  @Input('appCopyToClipboard') value: string;

  private badge: HTMLElement | null = null;
  private fadeOutTimeout: ReturnType<typeof setTimeout> | null = null;
  private removeTimeout: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.renderer.setStyle(this.el.nativeElement, 'cursor', 'pointer');
  }

  @HostListener('click')
  async onClick(): Promise<void> {
    if (!this.value) { return; }

    try {
      await navigator.clipboard.writeText(this.value);
      if (!this.destroyed) {
        this.showCopiedBadge();
      }
    } catch {
      // Clipboard API may fail in insecure contexts; silently ignore
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearTimers();
    this.removeBadge();
  }

  private showCopiedBadge(): void {
    this.clearTimers();
    this.removeBadge();

    // Remove any stale badges from previous instances
    this.document.querySelectorAll('.copy-badge').forEach(el => el.remove());

    const rect = this.el.nativeElement.getBoundingClientRect();

    this.badge = this.renderer.createElement('span');
    this.badge.innerHTML = `<span class="copy-badge-icon">&#10003;</span> Copied!`;
    this.renderer.addClass(this.badge, 'copy-badge');
    this.renderer.setStyle(this.badge, 'top', `${rect.top + window.scrollY - 4}px`);
    this.renderer.setStyle(this.badge, 'left', `${rect.left + rect.width / 2}px`);

    this.renderer.appendChild(this.document.body, this.badge);

    // Trigger reflow then add visible class for fade-in
    void this.badge.offsetWidth;
    this.renderer.addClass(this.badge, 'copy-badge--visible');

    this.fadeOutTimeout = setTimeout(() => {
      if (this.badge) {
        this.renderer.removeClass(this.badge, 'copy-badge--visible');
      }

      this.removeTimeout = setTimeout(() => {
        this.removeBadge();
      }, 300);
    }, 5000);
  }

  private removeBadge(): void {
    if (this.badge && this.badge.parentNode) {
      this.renderer.removeChild(this.badge.parentNode, this.badge);
    }
    this.badge = null;
  }

  private clearTimers(): void {
    if (this.fadeOutTimeout !== null) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }
    if (this.removeTimeout !== null) {
      clearTimeout(this.removeTimeout);
      this.removeTimeout = null;
    }
  }
}
